// CS-2018 - Super Admin EC2 dashboard: live status, guarded start/stop, audit-logged actions
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { ShieldAlert } from "lucide-react";
import CustomTable from "../common/CustomTable";
import Card from "../common/Card";
import AppButton from "../common/Button";
import ConfirmationActionModal from "../common/ConfirmationActionModal";
import AccessRestrictedComponent from "../common/AccessRestrictedComponent";
import Ec2AuditPopup from "./Ec2AuditPopup";
import { useModalStyles } from "../common/useModalStyles";
import { USER_ROLES } from "../../constants/applicationConstants";
import { SUCCESS_CODE } from "../../service/ServiceConstants";
import { useAuthSlice } from "../../redux/slice/authSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { setSingleAlertObj } from "../../redux/slice/commonSlice";
import {
  EC2_TRANSITIONAL_STATES,
  Ec2Instance,
  fetchEc2AuditLogs,
  fetchEc2Instances,
  startEc2,
  stopEc2,
  useEc2Slice,
} from "../../redux/slice/ec2/Ec2Slice";

// Status chip colors follow the app status palette (theme tokens .300 steps).
const STATUS_COLORS: Record<string, string> = {
  running: "#22C55E",
  pending: "#FFAB00",
  starting: "#FFAB00",
  initializing: "#FFAB00",
  stopping: "#00B8D9",
  "shutting-down": "#00B8D9",
  stopped: "#FF5630",
};

// Environment chips: soft tinted background + strong text. Only the app
// environments carry a hue — tooling boxes (BUILD / CI/CD / MIGRATION / UNKNOWN)
// fall back to neutral grey so the table doesn't turn into a rainbow.
const ENV_STYLES: Record<string, { bg: string; fg: string; fgDark: string }> = {
  PROD: { bg: "rgba(255,86,48,0.10)", fg: "#B71D18", fgDark: "#FF5630" },
  STAGE: { bg: "rgba(255,171,0,0.14)", fg: "#B76E00", fgDark: "#FFD666" },
  DEV: { bg: "rgba(20,184,166,0.12)", fg: "#0f766e", fgDark: "#14b8a6" },
  DEMO: { bg: "rgba(142,51,255,0.12)", fg: "#8E33FF", fgDark: "#C684FF" },
  AI: { bg: "rgba(0,184,217,0.12)", fg: "#006C9C", fgDark: "#61F3F3" },
};
const ENV_STYLE_FALLBACK = { bg: "rgba(145,158,171,0.16)", fg: "#637381", fgDark: "#919EAB" };

const REFRESH_INTERVAL_MS = 30000;
const TRANSITIONAL_REFRESH_INTERVAL_MS = 5000;

type PendingAction = {
  action: "start" | "stop";
  instance: Ec2Instance;
};

// Tapping a summary tile narrows the table to that status; tapping it again
// (or the Instances tile) shows everything.
type StatusFilter = "all" | "running" | "stopped" | "transitioning" | "locked";

const matchesStatusFilter = (instance: Ec2Instance, filter: StatusFilter) => {
  const state = instance.state?.toLowerCase() || "";
  switch (filter) {
    case "running":
      return state === "running";
    case "stopped":
      return state === "stopped";
    case "transitioning":
      return EC2_TRANSITIONAL_STATES.includes(state);
    case "locked":
      // Mirrors the tile count: only running instances show a locked Stop.
      return state === "running" && !instance.canStop;
    default:
      return true;
  }
};

const Ec2ServerControl = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const { dashboardData, fetchStatus, actionInstanceId, lastRefreshedAt, errorMessage, auditLogs, auditLogsFetchStatus } =
    useEc2Slice();
  const { tokens, inputFieldSx } = useModalStyles();

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [stopConfirmText, setStopConfirmText] = useState("");
  const [auditInstance, setAuditInstance] = useState<Ec2Instance | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Glassmorphism card + table treatment shared with the modernized list
  // pages (e.g. the Agent/JobSchedulerList page): rounded corners, subtle
  // border, backdrop blur, layered shadow, cyan-accented table header.
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const outlineBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap" as const,
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
  };

  const serverTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
      maxHeight: { xs: 420, md: "calc(100vh - 300px)" },
    },
    "& table": { background: "transparent !important", borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
      backgroundColor: `${listPanelBg} !important`,
      backdropFilter: "blur(8px)",
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": { color: tokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiTypography-root": { fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: tokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: tokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: tokens.accent, borderColor: tokens.accent } },
  };

  // Super Admin only — the SERVER_MANAGEMENT token gates the toolbar icon, this
  // gates direct navigation to /server_management.
  const hasServerAccess = userInfo.userRole === USER_ROLES.ROLE_SUPERADMIN;

  const instances = useMemo(() => dashboardData?.instances ?? [], [dashboardData]);
  const hasTransitionalInstance = useMemo(
    () => instances.some((instance) => EC2_TRANSITIONAL_STATES.includes(instance.state?.toLowerCase())),
    [instances],
  );

  const refreshInstances = useCallback(() => {
    dispatch(fetchEc2Instances());
  }, [dispatch]);

  // Background polling variant — skips the global and local loaders.
  const silentRefreshInstances = useCallback(() => {
    dispatch(fetchEc2Instances({ silent: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!hasServerAccess) return;
    refreshInstances();
  }, [hasServerAccess, refreshInstances]);

  // Live status polling — faster while any instance is starting/stopping.
  useEffect(() => {
    if (!hasServerAccess) return;
    const interval = setInterval(
      silentRefreshInstances,
      hasTransitionalInstance ? TRANSITIONAL_REFRESH_INTERVAL_MS : REFRESH_INTERVAL_MS,
    );
    return () => clearInterval(interval);
  }, [hasServerAccess, hasTransitionalInstance, silentRefreshInstances]);

  const closeConfirmation = () => {
    setPendingAction(null);
    setStopConfirmText("");
  };

  const executePendingAction = async () => {
    if (!pendingAction) return;
    const { action, instance } = pendingAction;
    closeConfirmation();

    const result: any = await dispatch(
      action === "stop"
        ? stopEc2({ instanceId: instance.instanceId, name: instance.name })
        : startEc2({ instanceId: instance.instanceId, name: instance.name }),
    );

    const failed = !!result?.error || result?.payload?.code !== SUCCESS_CODE;
    dispatch(
      setSingleAlertObj({
        message: failed
          ? result?.payload?.message || `Failed to ${action} ${instance.name}`
          : `${action === "stop" ? "Stop" : "Start"} requested for ${instance.name}. The action has been audit logged.`,
        alertType: failed ? "error" : "success",
      }),
    );
    refreshInstances();
  };

  const openAuditHistory = (instance: Ec2Instance) => {
    setAuditInstance(instance);
    dispatch(fetchEc2AuditLogs({ instanceId: instance.instanceId, name: instance.name }));
  };

  // Outlined chip with a status dot — reads at a glance without a solid color block.
  const statusChip = (instance: Ec2Instance) => {
    const state = instance.state?.toLowerCase() || "";
    const label = instance.status || instance.state || "Unknown";
    const color = STATUS_COLORS[state] || "#919EAB";
    return (
      <Chip
        size="small"
        variant="outlined"
        icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, ml: "8px !important" }} />}
        label={label.charAt(0).toUpperCase() + label.slice(1)}
        sx={{ color, borderColor: color, fontWeight: 600, backgroundColor: "transparent" }}
      />
    );
  };

  // Environment + brand: tinted environment chip (shield when Stop is guarded),
  // "CURRENT" marker for the environment this session runs on, and the product
  // brand (V3 BioMed / WoundWave / MPM / MedAudit / DevOps) underneath.
  const environmentCell = (instance: Ec2Instance) => {
    const env = instance.environment || "UNKNOWN";
    const style = ENV_STYLES[env] || ENV_STYLE_FALLBACK;
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
          <Tooltip
            title={instance.protectedEnvironment ? "Protected environment — Stop is disabled" : ""}
            arrow
          >
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                backgroundColor: style.bg,
                color: isDark ? style.fgDark : style.fg,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {instance.protectedEnvironment && <ShieldAlert size={12} />}
              {env}
            </Box>
          </Tooltip>
          {instance.currentEnvironment && (
            <Tooltip title="You are logged into this environment — Stop is disabled" arrow>
              <Box
                component="span"
                sx={{
                  px: 0.7,
                  py: 0.15,
                  borderRadius: "4px",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  border: `1px solid ${tokens.accent}`,
                  color: tokens.accent,
                }}
              >
                CURRENT
              </Box>
            </Tooltip>
          )}
        </Box>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: "0.64rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: tokens.textSecondary,
          }}
        >
          {instance.brand || "—"}
        </Typography>
      </Box>
    );
  };

  // Soft tonal buttons: tinted background + strong text. More presence than a
  // plain outline, calmer than the solid gradient buttons.
  const tonalBtnSx = (fg: string, fgDark: string, bg: string, bgHover: string) => ({
    py: 0.5,
    px: 1.4,
    fontSize: "0.78rem",
    textTransform: "none" as const,
    fontWeight: 700,
    borderRadius: "8px",
    color: isDark ? fgDark : fg,
    backgroundColor: bg,
    boxShadow: "none",
    "&:hover": { backgroundColor: bgHover, boxShadow: "none" },
  });

  const actionButton = (instance: Ec2Instance) => {
    const state = instance.state?.toLowerCase() || "";
    const isTransitional = EC2_TRANSITIONAL_STATES.includes(state);
    const isActionInFlight = actionInstanceId === instance.instanceId;

    if (isTransitional || isActionInFlight) {
      return (
        <Button size="small" disabled startIcon={<CircularProgress size={14} color="inherit" />}
          sx={tonalBtnSx("#637381", "#919EAB", "rgba(145,158,171,0.14)", "rgba(145,158,171,0.14)")}>
          {instance.status || "Working"}…
        </Button>
      );
    }

    if (state === "stopped") {
      return (
        <Button
          size="small"
          startIcon={<PlayArrowIcon sx={{ fontSize: "1.05rem !important" }} />}
          sx={tonalBtnSx("#118D57", "#77ED8B", "rgba(34,197,94,0.14)", "rgba(34,197,94,0.26)")}
          onClick={() => setPendingAction({ action: "start", instance })}
        >
          Start
        </Button>
      );
    }

    if (!instance.canStop) {
      // Guard from the backend: current environment or protected environment.
      // Dashed outline + lock so it cannot be mistaken for a clickable button.
      return (
        <Tooltip title={instance.stopBlockedReason || "Stop is not allowed for this server"} arrow>
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled
              startIcon={<LockOutlinedIcon sx={{ fontSize: "0.9rem !important" }} />}
              sx={{
                py: 0.5,
                fontSize: "0.78rem",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderStyle: "dashed",
                // MUI's default disabled grey is near-invisible on the light card.
                "&.Mui-disabled": {
                  borderStyle: "dashed",
                  color: isDark ? "#919EAB" : "#637381",
                  borderColor: isDark ? "rgba(145,158,171,0.4)" : "rgba(99,115,129,0.5)",
                },
              }}
            >
              Locked
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <Button
        size="small"
        startIcon={<StopIcon sx={{ fontSize: "1.05rem !important" }} />}
        sx={tonalBtnSx("#B71D18", "#FF5630", "rgba(255,86,48,0.12)", "rgba(255,86,48,0.24)")}
        onClick={() => setPendingAction({ action: "stop", instance })}
      >
        Stop
      </Button>
    );
  };

  const statTiles = useMemo(() => {
    const running = instances.filter((instance) => instance.state?.toLowerCase() === "running").length;
    const stopped = instances.filter((instance) => instance.state?.toLowerCase() === "stopped").length;
    const transitioning = instances.filter((instance) =>
      EC2_TRANSITIONAL_STATES.includes(instance.state?.toLowerCase()),
    ).length;
    // Only running instances can show a locked Stop button — stopped/transitioning
    // ones render Start or a spinner, so counting their guards would inflate the tile.
    const stopLocked = instances.filter(
      (instance) => instance.state?.toLowerCase() === "running" && !instance.canStop,
    ).length;
    return [
      { label: "Instances", value: instances.length, color: null, filter: "all" as StatusFilter },
      { label: "Running", value: running, color: "#22C55E", filter: "running" as StatusFilter },
      { label: "Stopped", value: stopped, color: "#FF5630", filter: "stopped" as StatusFilter },
      { label: "Transitioning", value: transitioning, color: "#FFAB00", filter: "transitioning" as StatusFilter },
      { label: "Locked", value: stopLocked, color: null, locked: true, filter: "locked" as StatusFilter },
    ];
  }, [instances]);

  // Counts always reflect the full fleet; only the table narrows down.
  const visibleInstances = useMemo(
    () => (statusFilter === "all" ? instances : instances.filter((instance) => matchesStatusFilter(instance, statusFilter))),
    [instances, statusFilter],
  );

  const tableRows = visibleInstances.map((instance) => ({
    uniqueId: instance.instanceId,
    server: (
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: tokens.textPrimary }}>
          {instance.name || instance.instanceId}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, fontFamily: "ui-monospace, Consolas, monospace" }}>
          {instance.instanceId}
        </Typography>
      </Box>
    ),
    environment: environmentCell(instance),
    // Column id is "state" (not "status") — CustomTable's mobile card view wraps
    // any *status* column in its own outlined chip, which double-wraps ours.
    state: statusChip(instance),
    type: instance.type || "—",
    elasticIp: instance.elasticIp || "—",
    uptime: instance.uptime || "—",
    // Info-tinted tonal button — visibly clickable, unlike the grey outlined
    // look that read like a disabled control.
    audit: (
      <Button
        size="small"
        startIcon={<HistoryIcon sx={{ fontSize: "1rem !important" }} />}
        onClick={() => openAuditHistory(instance)}
        sx={tonalBtnSx("#006C9C", "#61F3F3", "rgba(0,184,217,0.12)", "rgba(0,184,217,0.24)")}
      >
        History
      </Button>
    ),
    instanceObj: instance,
  }));

  if (!hasServerAccess) {
    return <AccessRestrictedComponent />;
  }

  const stopTargetName = pendingAction?.instance.name || pendingAction?.instance.instanceId || "";
  const isStopConfirmation = pendingAction?.action === "stop";

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", px: { xs: 1, md: 3 }, py: 2 }}>
      {/* Reachability notice — shown when the control service failed but stale data is on screen */}
      {fetchStatus === "error" && instances.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.8,
            py: 1,
            borderRadius: "10px",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "#B76E00",
            backgroundColor: "rgba(255,171,0,0.14)",
            border: "1px solid rgba(255,171,0,0.35)",
          }}
        >
          {errorMessage || "Unable to reach the EC2 control service."} Showing the last known statuses
          {lastRefreshedAt && ` from ${new Date(lastRefreshedAt).toLocaleTimeString()}`}.
        </Box>
      )}

      {/* Stat tiles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        {statTiles.map((tile) => (
          <Card
            key={tile.label}
            onClick={() => setStatusFilter((prev) => (prev === tile.filter ? "all" : tile.filter))}
            sx={{
              ...listCardSx,
              padding: { xs: "12px 14px", sm: "12px 14px" },
              cursor: "pointer",
              userSelect: "none",
              borderColor: statusFilter === tile.filter && tile.filter !== "all" ? tokens.accent : listBorderColor,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:hover": { borderColor: tokens.accent, boxShadow: `0 0 0 3px ${isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.12)"}` },
            }}
          >
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: tokens.textPrimary, fontVariantNumeric: "tabular-nums" }}>
              {tile.value}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.textSecondary,
                display: "flex",
                alignItems: "center",
                gap: 0.6,
              }}
            >
              {tile.color && (
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: tile.color }} />
              )}
              {tile.locked && <LockOutlinedIcon sx={{ fontSize: "0.8rem" }} />}
              {tile.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Server list — same glassmorphism Card + header layout as the Agent page */}
      <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
        <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
            <Grid item xs={12} md>
              <Typography variant="h3" fontWeight={800} color="text.primary">
                EC2 Servers
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, mt: 0.3 }}>
                Logged into: <b>{dashboardData?.currentEnvironment || "—"}</b>
                {lastRefreshedAt && ` · Last refreshed ${new Date(lastRefreshedAt).toLocaleTimeString()}`}
                {statusFilter !== "all" && ` · Showing ${visibleInstances.length} of ${instances.length} servers`}
              </Typography>
            </Grid>
            <Grid item xs={12} sm="auto">
              <AppButton
                variant="outlined"
                color="info"
                onClick={refreshInstances}
                disabled={fetchStatus === "loading"}
                startIcon={
                  fetchStatus === "loading" ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
                }
                fullWidth
                sx={outlineBtnSx}
              >
                Refresh
              </AppButton>
            </Grid>
            <Grid item xs={12}>
              <CustomTable
                columns={[
                  { id: "server", label: "Server" },
                  { id: "environment", label: "Environment" },
                  { id: "state", label: "Status" },
                  { id: "type", label: "Type" },
                  { id: "elasticIp", label: "Elastic IP" },
                  { id: "uptime", label: "Uptime" },
                  { id: "action", label: "Action" },
                  { id: "audit", label: "Audit" },
                ]}
                rows={tableRows}
                uniqueValue="uniqueId"
                disablePagination
                borderBox={false}
                // Internal scroll: cap the table's own container so the body
                // scrolls under the sticky header instead of the whole page.
                // Height tracks the viewport so tiles + card header stay on screen.
                sx={serverTableSx}
                emptyText={
                  fetchStatus === "loading"
                    ? "Loading servers…"
                    : fetchStatus === "error"
                      ? errorMessage || "Unable to reach the EC2 control service. Please try again."
                      : statusFilter !== "all" && instances.length > 0
                        ? "No servers match the selected status — tap the tile again to clear the filter"
                        : "No EC2 instances found"
                }
                // Wrapped in a Box: the mobile card ⋮ menu maps over the action
                // component's CHILDREN, so a bare button would render an empty menu.
                actionComponent={(row: any) => <Box>{actionButton(row.instanceObj)}</Box>}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Bottom spacing so the last row doesn't hug the viewport edge */}
      <Box mb={4} />

      {/* Confirmation — Stop requires typing the server name */}
      <ConfirmationActionModal
        open={pendingAction !== null}
        onClose={closeConfirmation}
        onConfirm={executePendingAction}
        title={isStopConfirmation ? `Stop ${stopTargetName}?` : `Start ${stopTargetName}?`}
        subtitle={
          pendingAction
            ? `${pendingAction.instance.brand} · ${pendingAction.instance.environment} · ${pendingAction.instance.instanceId}`
            : ""
        }
        confirmText={isStopConfirmation ? "Stop Server" : "Start Server"}
        cancelText="Cancel"
        confirmDisabled={isStopConfirmation && stopConfirmText !== stopTargetName}
        headerIcon={
          isStopConfirmation ? <StopIcon sx={{ color: tokens.error }} /> : <PlayArrowIcon sx={{ color: tokens.accent }} />
        }
        message={
          isStopConfirmation ? (
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={{ fontSize: "0.9rem", color: tokens.textSecondary }}>
                Stopping this server will disconnect all active users on{" "}
                <strong>{pendingAction?.instance.environment}</strong>. This action is recorded in the audit log.
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: tokens.textPrimary, mt: 1.5, mb: 0.5 }}>
                Type <strong>{stopTargetName}</strong> to confirm:
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                autoComplete="off"
                placeholder={stopTargetName}
                value={stopConfirmText}
                onChange={(event) => setStopConfirmText(event.target.value)}
                sx={inputFieldSx(false)}
              />
            </Box>
          ) : (
            <>
              The server will boot and typically becomes reachable in 1–2 minutes. This action is recorded in the
              audit log.
            </>
          )
        }
      />

      {/* Per-instance audit history */}
      <Ec2AuditPopup
        open={auditInstance !== null}
        onClose={() => setAuditInstance(null)}
        instance={auditInstance}
        logs={auditLogs}
        loading={auditLogsFetchStatus === "loading"}
      />
    </Box>
  );
};

export default Ec2ServerControl;
